import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { StarRating } from "@/components/StarRating";
import { useAuth } from "@/context/AuthContext";

/**
 * Aggregate rating block. Visibility is enforced by the backend — this component
 * simply hides itself when the response returns `count: 0` (either genuinely empty
 * or gated by the viewer's role).
 *
 * Backend rules:
 *  - Anon caller               → count:0 (hidden)
 *  - Individual viewing an individual → count:0 (hidden, unless self)
 *  - Individual viewing an org        → count:0 unless… actually: any logged-in user
 *    can see an org's rating. If we render nothing when count:0, that's fine —
 *    it just won't appear for orgs with zero reviews yet.
 *  - Org viewing any user             → full
 *  - Self                             → full
 */
export function RatingSummary({ userId }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!userId) return;
    api.feedbackForUser(userId).then(setData).catch(() => setData(null));
  }, [userId, user?.user_id]);

  if (!data || !data.count) return null;

  return (
    <div data-testid="profile-rating-summary" className="bg-white border border-neutral-200 rounded-2xl p-5">
      <div className="flex items-center gap-3">
        <StarRating value={data.average_rating || 0} readonly size={22} />
        <div className="text-sm text-neutral-700">
          <b data-testid="profile-rating-value">{data.average_rating?.toFixed(1)}</b>
          <span className="text-neutral-500"> · </span>
          <span data-testid="profile-rating-count">{data.count} review{data.count === 1 ? "" : "s"}</span>
        </div>
        <button
          data-testid="profile-reviews-toggle"
          onClick={() => setExpanded(e => !e)}
          className="ml-auto text-xs text-forest hover:underline"
        >
          {expanded ? "Hide reviews" : "Show reviews"}
        </button>
      </div>
      {expanded && (
        <ul className="mt-4 space-y-3">
          {data.reviews.map(r => (
            <li key={r.id} data-testid={`review-item-${r.id}`} className="border-l-2 border-amber-300 pl-3">
              <div className="flex items-center gap-2 text-xs text-neutral-500">
                <StarRating value={r.rating} readonly size={14} />
                <span>· {r.author_name}</span>
                <span>· {new Date(r.created_at).toLocaleDateString()}</span>
              </div>
              {r.text && <div className="text-sm text-neutral-700 mt-1 whitespace-pre-line">{r.text}</div>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default RatingSummary;
